import React from 'react';

import { useExportTemplate, useStatusTemplates } from 'Templates';

import {
  Action,
  Actions,
  FileDownloadIcon,
  DescriptionIcon,
  FileUploadIcon,
  ActionBar,
} from '@gogame/components';
import { FullScreenToggler } from '@gogame/data-grid';
import { useChooseFiles, useChildComponent } from '@gogame/hooks-ts';
import {
  SearchField,
  useScreenGo,
  useScreenState,
  useScreenTitle,
} from '@gogame/screens';

import { CampaignFormCreateWithChaining } from 'scenes/Campaigns/GGCampaigns/components/CampaignForm';
import { CAMPAIGN_IDS } from 'scenes/Campaigns/GGCampaigns/components/OwnersManagement';
import {
  TEMPLATE_NEW_CAMPAIGN,
  TEMPLATE_UPDATE_CAMPAIGN,
  TEMPLATE_UPDATE_INFLUENCER_CAMPAIGN,
} from 'scenes/Campaigns/constants';
import {
  useExportCampaigns,
  useUploadNewCampaigns,
  useUploadUpdatedCampaigns,
} from 'scenes/Campaigns/GGCampaigns/data/mutations';
import { parentCampaignDetailsLink } from 'scenes/Campaigns/ParentCampaigns/navigation';

export default () => {
  const [childComponent, openChildComponent] = useChildComponent();
  const go = useScreenGo();
  const [screenTitle] = useScreenTitle();
  const chooseFiles = useChooseFiles({ accept: '.csv' });
  const screenState = useScreenState();
  const [exportCampaigns] = useExportCampaigns();
  const [uploadUpdatedCampaigns] = useUploadUpdatedCampaigns();
  const [uploadNewCampaigns] = useUploadNewCampaigns();
  const [exportNewCampaignsTemplate] = useExportTemplate({
    name: TEMPLATE_NEW_CAMPAIGN,
  });
  const [exportUpdatedCampaignsTemplate] = useExportTemplate({
    name: TEMPLATE_UPDATE_CAMPAIGN,
  });
  const [exportUpdatedInfluencerCampaignsTemplate] = useExportTemplate({
    name: TEMPLATE_UPDATE_INFLUENCER_CAMPAIGN,
  });

  const handleUploadNewCampaigns = async () => {
    const [file] = await chooseFiles();

    await uploadNewCampaigns(file);
  };

  const handleUploadUpdatedCampaigns = async () => {
    const [file] = await chooseFiles();

    await uploadUpdatedCampaigns(file);
  };

  const handleExportNewCampaigns = async () => {
    await exportNewCampaignsTemplate();
  };

  const handleExportUpdatedCampaigns = async () => {
    await exportUpdatedCampaignsTemplate();
  };
  const handleExportUpdatedInfluencerCampaigns = async () => {
    await exportUpdatedInfluencerCampaignsTemplate();
  };

  const handleExport = async (selectedRows) => {
    const { filters, tableSettings, searchQuery } = screenState;

    const params = {
      filters,
      tableSettings,
      searchQuery,
    };

    if (selectedRows?.length) {
      params[CAMPAIGN_IDS] = selectedRows.map(({ id }) => id);
    }

    await exportCampaigns(params);
  };
  const handleCreate = async ({ initialValues } = {}) => {
    openChildComponent(CampaignFormCreateWithChaining, {
      initialValues: initialValues,
      onSubmit: (campaign) => {
        if (campaign?.parentCampaign?.id) {
          go(
            `../${parentCampaignDetailsLink.absolutePath.replace(':id', campaign.parentCampaign.id)}`
          );
          return;
        }

        go(`${campaign.id}`);
      },
    });
  };

  const templateNewUpdated = useStatusTemplates({
    name: TEMPLATE_NEW_CAMPAIGN,
  });

  const templateUpdateUpdated = useStatusTemplates({
    name: TEMPLATE_UPDATE_CAMPAIGN,
  });

  const templateUpdateInflCampaignUpdated = useStatusTemplates({
    name: TEMPLATE_UPDATE_INFLUENCER_CAMPAIGN,
  });

  return (
    <>
      <ActionBar
        title={screenTitle}
        search={
          <SearchField tooltip={'Search by name or id'} applyFilters={true} />
        }
        secondaryActions={
          <Actions>
            <Action
              icon={<FileDownloadIcon />}
              label={'Export Campaigns'}
              onClick={handleExport}
            />
            <Action icon={<FileUploadIcon />} tooltip={'Import Campaigns'}>
              <Action
                label={'Import new campaigns'}
                onClick={handleUploadNewCampaigns}
              />
              <Action
                label={'Import updated campaigns'}
                onClick={handleUploadUpdatedCampaigns}
              />
              <Action
                label={'Import Influencer updated campaigns'}
                onClick={handleUploadUpdatedCampaigns}
              />
            </Action>
            <Action icon={<DescriptionIcon />} tooltip={'Download Template'}>
              <Action
                label={'Download template for new campaigns'}
                onClick={handleExportNewCampaigns}
                badge={templateNewUpdated ? 'Updated' : null}
              />
              <Action
                label={'Download template for updating campaigns'}
                onClick={handleExportUpdatedCampaigns}
                badge={templateUpdateUpdated ? 'Updated' : null}
              />
              <Action
                label={'Download template for updating Influencer campaigns'}
                onClick={handleExportUpdatedInfluencerCampaigns}
                badge={templateUpdateInflCampaignUpdated ? 'Updated' : null}
              />
            </Action>

            <Action component={FullScreenToggler} />
          </Actions>
        }
        primaryActions={
          <Actions>
            <Action
              border={true}
              button={true}
              add={true}
              iconContent={true}
              tooltip={'Add Campaign'}
              variant={'outlined'}
              onClick={handleCreate}
            />
          </Actions>
        }
      />
      {childComponent}
    </>
  );
};
